import { useState, useRef, useEffect } from "react";
import { askBrain, findByName, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton, PhotoChip } from "./photo";
import { store } from "../store";
import { cloudBackupStatus } from "../brain";
import { executeAction, type ChatAction, type FilterRow } from "./actions";
import CallOverlay from "../live/CallOverlay";

const STORE_KEY = "oriora.oriora_closet.chat.v1";
const GREETING: ChatTurn = { role: "assistant", content: "Tell me what to do — I'll add an item, log a wear, search, rename, summarize, or delete, no tapping around." };

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const p = JSON.parse(raw); if (Array.isArray(p) && p.length) return p; }
  } catch {}
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch {}
}

export default function Chat({ onChanged }: { onChanged?: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  const [pending, setPending] = useState<ChatAction | null>(null);
  // A photo picked in the composer, waiting to be filed onto a garment. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  // A photo turn is filed ON-DEVICE onto a garment (its wardrobe photo): no model call, no cost.
  // The user names the item in the same message; fuzzy name-resolution finds the closest one.
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "📷 photo" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      const q = text.trim();
      if (!q) {
        setTurns([...next, { role: "assistant", content: "Which item is this photo for? Name it, then send." }]);
        return; // keep the photo attached so the next message can name it
      }
      const matches = await findByName(q);
      if (matches.length === 0) {
        setTurns([...next, { role: "assistant", content: `I couldn't find an item matching "${q}". Add it first, then I'll attach the photo.` }]);
        return; // keep the photo attached
      }
      if (matches.length > 1) {
        const list = matches.slice(0, 6).map((g) => `• ${g.item}${g.brand ? ` · ${g.brand}` : ""}`).join("\n");
        setTurns([...next, { role: "assistant", content: `More than one item matches — which one?\n${list}` }]);
        return; // keep the photo attached
      }
      const target = matches[0];
      await store.updateGarment(target.id, { photo_ref: photo! });
      setPhoto(null);
      onChanged?.();
      setTurns([...next, { role: "assistant", content: `📷 Added your photo to ${target.item}.` }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, onChunk);
      const transcript = next.map((t) => `${t.role}: ${t.content}`).join("\n");
      const needsConfirm = (a: ChatAction) => a.kind === "backup_restore" || a.kind === "import_file" || a.kind === "delete_garment" ||
        a.kind === "library_remove" || a.kind === "library_merge";
      const warnFor = (a: ChatAction) =>
        
  a.kind === "library_merge"
    ? `Merge ${a.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`
    : a.kind === "library_remove"
      ? `Remove ${a.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`
      : a.kind === "import_file" ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
        : a.kind === "backup_restore" ? `Restore will REPLACE everything in the app with your ${a.provider === "gdrive" ? "Google Drive" : a.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
        : `Delete ${(a as { label?: string }).label ?? "this"}? Tap Confirm, or Cancel.`;

      // Execute every NON-confirm action the brain returned, in order (multiple items in one message
      // → one action each). A single confirm-required action keeps the existing confirm-then-run UX.
      const msgs: string[] = [];
      let changed = false;
      let rows: FilterRow[] | undefined;
      for (const action of actions) {
        if (needsConfirm(action)) continue;
        const result = await executeAction(action, { transcript });
        if (result.message) msgs.push(result.message);
        if (result.changed) changed = true;
        if (result.rows) rows = result.rows;
      }
      if (changed) onChanged?.();

      // If a destructive action is present, pend it (after doing the safe ones) for a Confirm tap.
      let confirmAction = actions.find(needsConfirm);
      if (confirmAction) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        if (confirmAction.kind === "backup_restore" && !confirmAction.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) confirmAction = { ...confirmAction, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        setPending(confirmAction);
        setTurns([...next, { role: "assistant", content: [reply, ...msgs, warnFor(confirmAction)].filter(Boolean).join("\n\n") }]);
        setBusy(false);
        return;
      }
      setTurns([...next, { role: "assistant", content: [reply, ...msgs].filter(Boolean).join("\n\n") || "Done.", rows }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    try {
      const result = await executeAction(pending);
      setTurns((t) => [...t, { role: "assistant", content: result.message || "Done." }]);
      if (result.changed) onChanged?.();
    } catch (e) {
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setPending(null); setBusy(false);
    }
  }
  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it." }]);
  }

  return (
    <div className="ocl-chat">
      <div className="ocl-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`ocl-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`ocl-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
              {t.rows && t.rows.length > 0 && <FilterRows rows={t.rows} />}
            </div>
          </div>
        ))}
        {busy && <div className="ocl-msgrow"><div className="ocl-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="ocl-composer ocl-confirm">
          <button className="btn btn-primary" style={{ background: "var(--rose, #c0392b)", borderColor: "var(--rose, #c0392b)" }} onClick={confirmPending} disabled={busy}>{pending?.kind && String(pending.kind).startsWith("delete") ? "Confirm delete" : "Confirm"}</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <>
          {photo && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
          <div className="ocl-composer">
            <textarea value={input} placeholder={photo ? "Name the item…" : "Tell me what to do…"} rows={2} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <PhotoButton onPhoto={setPhoto} disabled={busy} />
            <MicButton value={input} onChange={setInput} disabled={busy} />
            <button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position
                sheet, so it renders correctly anywhere in the tree, and this needs no
                structural analysis of each app's JSX — a button whose overlay never got
                mounted is the half-wired failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
            <button className="btn btn-primary" onClick={send} disabled={busy}>Send</button>
          </div>
        </>
      )}
    </div>
  );
}

function FilterRows({ rows }: { rows: FilterRow[] }) {
  return (
    <div className="ocl-rows">
      {rows.map((r) => (
        <div key={r.id} className="ocl-rowitem">
          <span>{r.item}{r.brand ? ` · ${r.brand}` : ""}</span>
          <span className="tnum">{r.wears}×</span>
        </div>
      ))}
    </div>
  );
}
